'use client';

import { forwardRef, useState } from 'react';
import { Link, Typography, styled } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { useThemeProps } from '@mui/material/styles';

const ELLIPSIS = '…';

export interface TextTruncatorProps extends TypographyProps {
    children: string;
    charLimit?: number;
    showMore?: string;
    showLess?: string;
    expandedByDefault?: boolean;
}

const TextTruncatorRoot = styled(Typography, {
    name: 'MuiTextTruncator',
    slot: 'root',
})({});

export const TextTruncator = forwardRef<HTMLElement, TextTruncatorProps>(
    function TextTruncator(inProps, ref) {
        const props = useThemeProps({ props: inProps, name: 'MuiTextTruncator' });
        const {
            children: text,
            charLimit = 200,
            showMore = 'Expand',
            showLess = 'Collapse',
            expandedByDefault,
            ...typographyProps
        } = props;
        const [showFullText, setShowFullText] = useState(expandedByDefault);

        if (charLimit >= text.length) {
            return (
                <TextTruncatorRoot ref={ref} {...typographyProps}>
                    {text}
                </TextTruncatorRoot>
            );
        }

        const substringEnd = showFullText
            ? text.length
            : text.substring(0, charLimit - ELLIPSIS.length).search(/ [^ ]*$/);
        return (
            <TextTruncatorRoot ref={ref} {...typographyProps}>
                {text.substring(0, substringEnd)}
                {!showFullText ? ELLIPSIS : null}
                &nbsp;
                <Link
                    component="button"
                    variant={typographyProps.variant}
                    onClick={() => {
                        setShowFullText(!showFullText);
                    }}
                    sx={{ verticalAlign: 'top' }}>
                    {showFullText ? showLess : showMore}
                </Link>
            </TextTruncatorRoot>
        );
    }
);
